/**
 * 77. Fixed Asset Maintenance — asset register; maintenance
 * schedule; work orders.
 */
import {
  Card,
  PageHeader,
  Button,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { formatDate } from "@/lib/money";

interface MaintenanceRow {
  id: string;
  workOrderNumber: string;
  assetCode: string;
  assetName: string;
  maintenanceType: "Preventive" | "Corrective" | "Inspection";
  scheduledDate: string | null;
  completedDate: string | null;
  assignee: string;
  status: "Open" | "InProgress" | "Completed" | "Cancelled";
}

export function AssetMaintenancePage() {
  const { data, isLoading } = useApiList<MaintenanceRow>(
    ["maintenance", "work-orders"],
    "/maintenance/work-orders",
  );

  return (
    <div>
      <PageHeader
        title="Fixed Asset Maintenance"
        subtitle="Asset register, maintenance schedules, and work orders."
        actions={<Button variant="primary">New work order</Button>}
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Work orders (${data.total})`}>
          <DataTable<MaintenanceRow>
            rows={data.items}
            columns={[
              {
                key: "workOrderNumber",
                label: "Work order",
                render: (r) => (
                  <span className="atlas-mono">{r.workOrderNumber}</span>
                ),
              },
              {
                key: "assetCode",
                label: "Asset",
                render: (r) => (
                  <span className="atlas-mono">{r.assetCode}</span>
                ),
              },
              { key: "assetName", label: "Asset name" },
              { key: "maintenanceType", label: "Type" },
              {
                key: "scheduledDate",
                label: "Scheduled",
                render: (r) =>
                  r.scheduledDate ? formatDate(r.scheduledDate) : "—",
              },
              {
                key: "completedDate",
                label: "Completed",
                render: (r) =>
                  r.completedDate ? formatDate(r.completedDate) : "—",
              },
              { key: "assignee", label: "Assignee" },
              {
                key: "status",
                label: "Status",
                render: (r) =>
                  r.status === "Completed" ? (
                    <StatusBadge status={r.status} />
                  ) : r.status === "Cancelled" ? (
                    <Badge tone="danger">{r.status}</Badge>
                  ) : r.status === "InProgress" ? (
                    <Badge tone="primary">{r.status}</Badge>
                  ) : (
                    <Badge tone="warning">{r.status}</Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
